import { createContext, useContext, useState, useCallback, useRef } from 'react';
import Toast from '../components/common/Toast';

const ToastContext = createContext();

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export const ToastProvider = ({ children }) => {
    const [toast, setToast] = useState(null); // { id, message, type, duration } | null
    const timeoutRef = useRef(null);
    const idRef = useRef(0); // bumped on every toast so each one is a fresh React instance

    const showToast = useCallback((message, type = 'success', duration = 3000) => {
        // Clear any pending dismiss timeout from a previous toast
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Each toast gets a new id; combined with `key` on <Toast/> below, this
        // remounts the component so the progress-bar animation restarts from the left.
        idRef.current += 1;

        // Store duration too, so the progress bar can run for exactly the same time.
        setToast({ id: idRef.current, message, type, duration });

        // Schedule auto-dismiss
        timeoutRef.current = setTimeout(() => {
            setToast(null);
            timeoutRef.current = null;
        }, duration);
    }, []);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            <Toast
                key={toast?.id}
                message={toast?.message}
                type={toast?.type}
                duration={toast?.duration}
                onClose={() => setToast(null)}
            />
        </ToastContext.Provider>
    );
};